import { Button } from "@mui/material";
import { getMerchantConfig } from "features/config/configSlice";
import {
  updateReturnOrder,
  updateSelectedOrder,
  getSelectedOrder,
  getUUID,
} from "features/orders/orderSlice";
import {
  addPreviews,
  addProceedBool,
  addProductType,
  addSelectedReason,
  addSelectedSubReason,
  addUploads,
  addUploadMediaIds,
  setImageUploads,
} from "features/product/productSlice";
import {
  addRefundJSON,
  addRefundSource,
  addReturnedProducts,
  getPaymentDetails,
  getPaymentStatus,
  addPaymentDetails,
} from "features/returnedProducts/returnedProductSlice";
import { getEmail, getPhone } from "features/user/userSlice";
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FLUSH } from "redux-persist";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getEncipherencode } from "utils/encipherencode";
import styled from "styled-components";
import { clearReturnCartContext, getReturnCartContext } from "utils/returnCartSession";
import { readAgentDraftReceiptForOperation, clearAgentDraftReceipt, clearAgentDraftAttempt, validateAgentDraftResponse } from "utils/agentDraft";
import { hasValidTrackingReference, versionedPath } from "utils/routes";
import { V2TrackingBoneyardLoader } from "components/v2/Loaders/BoneyardLoaders";
import PageHeader from "components/v2/Common/PageHeader";
import CheckIcon from "@mui/icons-material/Check";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const StyledSuccess = styled.div`
  width: 100%;
  max-width: 500px;
  min-height: calc(100vh - 72px);
  margin: 0px auto;
  padding: 34px 20px 126px;
  background: var(--rms-v2-page-bg);
  color: var(--rms-v2-text-title);
  text-align: center;

  .v2-success-icon {
    width: 78px;
    height: 78px;
    margin: 0 auto 24px;
    border-radius: 50%;
    background: var(--rms-v2-success);
    color: var(--rms-v2-surface);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 16px 34px rgba(22, 172, 55, 0.24);
    animation: v2SuccessIconPop 360ms ease-out both;
  }

  .v2-success-icon svg {
    width: 34px;
    height: 34px;
    stroke-dasharray: 46;
    stroke-dashoffset: 46;
    animation: v2SuccessCheckDraw 520ms ease-out 160ms forwards;
  }

  @keyframes v2SuccessIconPop {
    0% {
      opacity: 0;
      transform: scale(0.72);
    }
    70% {
      opacity: 1;
      transform: scale(1.08);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes v2SuccessCheckDraw {
    to {
      stroke-dashoffset: 0;
    }
  }

  .v2-success-title {
    margin: 0;
    color: var(--rms-v2-text-title);
    font-size: 24px;
    font-weight: 900;
  }

  .v2-success-subtitle {
    max-width: 270px;
    margin: 12px auto 34px;
    color: var(--rms-v2-text-muted);
    font-size: 14px;
  }

  .v2-success-section-title {
    margin: 0 0 18px;
    color: var(--rms-v2-text-title);
    font-size: 16px;
    font-weight: 900;
    text-align: left;
  }

  .v2-next-card {
    padding: 22px 18px;
    background: var(--rms-v2-surface);
    border: 1px solid var(--rms-v2-border-soft);
    border-radius: ${(props) => (props.rounded ? "10px" : "0px")};
    box-shadow: 0 14px 34px rgba(29, 42, 64, 0.06);
    text-align: left;
  }

  .v2-next-item {
    display: grid;
    grid-template-columns: 36px 1fr;
    gap: 12px;
    align-items: start;
  }

  .v2-next-item + .v2-next-item {
    margin-top: 18px;
  }

  .v2-next-icon {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: var(--rms-v2-primary-soft);
    color: ${(props) => props.primaryColor};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .v2-next-icon svg {
    width: 18px;
    height: 18px;
  }

  .v2-next-title {
    margin: 0;
    color: var(--rms-v2-text-title);
    font-size: 14px;
    font-weight: 900;
  }

  .v2-next-copy {
    margin: 5px 0 0;
    color: var(--rms-v2-text-muted);
    font-size: 12px;
  }

  .v2-request-meta-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    margin: 24px 4px 0;
    text-align: left;
  }

  .v2-request-meta-label {
    margin: 0 0 8px;
    color: var(--rms-v2-text-muted);
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  .v2-request-meta-value {
    margin: 0;
    color: var(--rms-v2-text-title);
    font-size: 13px;
    font-weight: 500;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }

  .v2-success-action-bar {
    position: fixed;
    left: 50%;
    bottom: calc(28px + env(safe-area-inset-bottom, 0px));
    transform: translateX(-50%);
    width: 100%;
    max-width: 500px;
    padding: 14px 20px;
    background: var(--rms-v2-surface);
    border-top: 1px solid var(--rms-v2-border-soft);
    border-radius: ${(props) => (props.rounded ? "10px 10px 0 0" : "0px")};
    display: grid;
    grid-template-columns: 1fr 1.3fr;
    gap: 12px;
    z-index: 60;
  }

  .v2-success-action-bar button {
    height: 48px;
    min-height: 48px;
    border-radius: ${(props) => (props.rounded ? "10px" : "0px")};
    text-transform: none;
    font-size: 13px;
    font-weight: 900;
  }

  .v2-back-orders {
    box-shadow: none !important;
  }

  .v2-track-request {
    background: ${(props) => props.primaryColor} !important;
    color: var(--rms-v2-surface) !important;
    box-shadow: 0 12px 24px ${(props) => props.primarySoft} !important;
  }
`;

const withAlpha = (color, alpha = "33") =>
  /^#[0-9A-Fa-f]{6}$/.test(color || "") ? `${color}${alpha}` : "rgba(29, 42, 64, 0.16)";

const normalRequestId = (data) =>
  [data?.return_request_id, data?.request_id].find(hasValidTrackingReference);

function isCompletedOperation(data, operationId, requestIdParam) {
  const requestId = normalRequestId(data);
  return Boolean(operationId && requestId &&
    String(data?.completed_operation_id || "") === String(operationId) &&
    (!requestIdParam || String(requestIdParam) === String(requestId)) &&
    !["pending", "processing", "failed", "cancelled", "canceled"].includes(String(data?.payment_status || "").toLowerCase()));
}

function matchingAgentConfirmation(value, merchantId, orderId, operationId, requestId) {
  try {
    validateAgentDraftResponse(value);
    return String(value.merchant_id) === String(merchantId) &&
      (!orderId || String(value.order_id) === String(orderId)) &&
      Boolean(operationId && requestId) &&
      String(value.operation_id) === String(operationId) &&
      String(value.normal_request_id) === String(requestId) ? value : null;
  } catch (_) { return null; }
}

function persistedAgentConfirmation(confirmation) {
  try {
    const root = JSON.parse(sessionStorage.getItem("persist:root") || "null");
    const details = JSON.parse(root.returnedReducer).paymentDetails;
    return String(details.completed_operation_id) === confirmation.operation_id &&
      JSON.stringify(details.agent_draft_confirmation) === JSON.stringify(confirmation);
  } catch (_) { return false; }
}

function Success() {
  const dispatch = useDispatch();
  const userPhone = useSelector(getPhone);
  const userEmail = useSelector(getEmail);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const encipherencode = getEncipherencode(params);
  const requestIdParam = params.get("request_id");
  const referenceIdParam = params.get("reference_id");

  const [finishStatus, setfinishStatus] = useState(false);

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    if (!finishStatus) {
      setfinishStatus(true);
      let url = versionedPath("/orders");
      if (encipherencode) {
        url += `?encipherencode=${encipherencode}`;
      }
      navigate(url);
    }
  };

  const merchantConfig = useSelector(getMerchantConfig);
  const paymentDetails = useSelector(getPaymentDetails);
  const paymentStatus = useSelector(getPaymentStatus);
  const selectedOrder = useSelector(getSelectedOrder);
  const uniqueId = useSelector(getUUID);
  const storedContext = getReturnCartContext();
  const orderId = selectedOrder?.order_id || storedContext?.selectedOrder?.order_id;
  const currentOperationId = uniqueId || storedContext?.uniqueId;
  // Only a trusted create/payment completion may associate a draft with this normal request.
  // The current paymentDetails wins over an older paymentStatus left by an earlier request.
  const currentReceipt = readAgentDraftReceiptForOperation(merchantConfig?.merchant_id, orderId, currentOperationId);
  const responses = [paymentDetails, paymentStatus];
  const completedResponse = responses.find((data) => isCompletedOperation(data, currentOperationId, requestIdParam)) || responses.find((data) => {
    const completedId = data?.completed_operation_id;
    if (!isCompletedOperation(data, completedId, requestIdParam)) return false;
    // An earlier gateway may finish after a retry refreshed the cart UUID.
    // Associate it only with the same saved draft; a newer draft must stay separate.
    const completedReceipt = readAgentDraftReceiptForOperation(merchantConfig?.merchant_id, orderId, completedId);
    return Boolean(currentReceipt && completedReceipt?.draft_id === currentReceipt.draft_id);
  });
  const operationId = completedResponse?.completed_operation_id || currentOperationId;
  const completingEarlierOperation = String(operationId) !== String(currentOperationId);
  const completedRequestId = normalRequestId(completedResponse);
  const agentReceipt = readAgentDraftReceiptForOperation(merchantConfig?.merchant_id, orderId, operationId);
  const agentDraftId = agentReceipt?.draft_id;
  const agentDraftReference = agentReceipt?.reference_id;
  const storedConfirmation = matchingAgentConfirmation(paymentDetails?.agent_draft_confirmation,
    merchantConfig?.merchant_id, orderId, operationId, completedRequestId);
  const capturedConfirmation = useMemo(() => storedConfirmation || (agentDraftId && completedRequestId ? {
    draft_id: agentDraftId, reference_id: agentDraftReference, status: "draft",
    merchant_id: String(merchantConfig.merchant_id), order_id: String(orderId),
    operation_id: String(operationId), normal_request_id: String(completedRequestId),
  } : null), [storedConfirmation, agentDraftId, agentDraftReference,
    completedRequestId, merchantConfig?.merchant_id, orderId, operationId]);
  const [agentConfirmation, setAgentConfirmation] = useState(() => capturedConfirmation);
  const confirmedAgent = matchingAgentConfirmation(agentConfirmation,
    merchantConfig?.merchant_id, orderId, operationId, completedRequestId) || storedConfirmation;
  const mixedOperationPending = Boolean(agentReceipt && !completedRequestId);
  const hasMixedOutcome = Boolean(agentReceipt || confirmedAgent);
  const successRequestId = hasMixedOutcome ? completedRequestId : [
    paymentStatus?.return_request_id,
    paymentStatus?.request_id,
    requestIdParam,
    paymentDetails?.return_request_id,
    paymentDetails?.request_id,
  ].find(hasValidTrackingReference);
  const successReferenceId = hasMixedOutcome ? completedResponse?.reference_id : [
    paymentDetails?.reference_id,
    paymentStatus?.reference_id,
    referenceIdParam,
  ].find(hasValidTrackingReference);

  useEffect(() => {
    const confirmation = capturedConfirmation;
    if (!confirmation || !completedResponse) return;
    let cancelled = false;
    setAgentConfirmation((current) => JSON.stringify(current) === JSON.stringify(confirmation) ? current : confirmation);
    if (JSON.stringify(paymentDetails?.agent_draft_confirmation) !== JSON.stringify(confirmation)) {
      dispatch(addPaymentDetails({ ...paymentDetails, ...completedResponse, agent_draft_confirmation: confirmation }));
    }
    const receiptToRetire = readAgentDraftReceiptForOperation(confirmation.merchant_id, confirmation.order_id, confirmation.operation_id);
    if (receiptToRetire?.draft_id === confirmation.draft_id) {
      const writes = [];
      dispatch({ type: FLUSH, result: (write) => writes.push(write) });
      Promise.all(writes).then(() => {
        if (cancelled || completingEarlierOperation || !persistedAgentConfirmation(confirmation)) return;
        const currentReceipt = readAgentDraftReceiptForOperation(confirmation.merchant_id, confirmation.order_id, confirmation.operation_id);
        if (currentReceipt?.draft_id !== confirmation.draft_id) return;
        clearAgentDraftReceipt(confirmation.merchant_id, confirmation.order_id);
        clearAgentDraftAttempt(confirmation.merchant_id, confirmation.order_id);
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [dispatch, merchantConfig?.merchant_id, orderId, operationId, completedRequestId,
    completedResponse, paymentDetails, capturedConfirmation, completingEarlierOperation]);

  useEffect(() => {
    if (
      merchantConfig?.merchant_id &&
      (successRequestId || successReferenceId) && !mixedOperationPending && !completingEarlierOperation
    ) {
      dispatch(updateSelectedOrder({}));
      dispatch(addSelectedReason(null));
      dispatch(addSelectedSubReason(null));
      dispatch(addUploads([]));
      dispatch(setImageUploads([]));
      dispatch(addUploadMediaIds([]));
      dispatch(addPreviews([]));
      dispatch(addReturnedProducts([]));
      dispatch(addProductType(null));
      dispatch(addProceedBool(false));
      dispatch(addRefundJSON({}));
      dispatch(addRefundSource(null));
      clearReturnCartContext();
    } else if (
      merchantConfig?.merchant_id &&
      userEmail === "" &&
      userPhone === "" &&
      !successRequestId &&
      !successReferenceId
    ) {
      let url = "/";
      if (encipherencode) {
        url += `?encipherencode=${encipherencode}`;
      }
      navigate(url);
    }
    window.history.pushState(
      null,
      null,
      encipherencode
          ? `${window.location.pathname}?${new URLSearchParams({
            encipherencode,
            ...(successRequestId ? { request_id: successRequestId } : {}),
            ...(successReferenceId
              ? { reference_id: successReferenceId }
              : {}),
          }).toString()}`
        : window.location.pathname
    );
    window.addEventListener("popstate", onBackButtonEvent);
    return () => {
      window.removeEventListener("popstate", onBackButtonEvent);
    };
  }, [
    dispatch,
    encipherencode,
    merchantConfig?.merchant_id,
    mixedOperationPending,
    completingEarlierOperation,
    paymentDetails,
    paymentStatus,
    referenceIdParam,
    requestIdParam,
    successReferenceId,
    successRequestId,
    userEmail,
    userPhone,
  ]);

  const handleTracking = () => {
    dispatch(updateReturnOrder(successRequestId || ""));
    const trackingParams = new URLSearchParams();

    if (encipherencode) {
      trackingParams.set("encipherencode", encipherencode);
    }
    if (successRequestId) {
      trackingParams.set("request_id", successRequestId);
    } else if (successReferenceId) {
      trackingParams.set("rid", successReferenceId);
    }

    const queryString = trackingParams.toString();
    const url = `${versionedPath("/tracking")}${
      queryString ? `?${queryString}` : ""
    }`;
    navigate(url);
  };

  const handleBackToOrders = () => {
    dispatch(updateReturnOrder(""));
    let url = versionedPath("/orders");
    if (encipherencode) {
      url += `?encipherencode=${encipherencode}`;
    }
    navigate(url);
  };

  const amountPaid = hasMixedOutcome ? completedResponse?.amount_to_pay || 0 :
    paymentDetails?.amount_to_pay || paymentStatus?.amount_to_pay || 0;
  const transactionId = hasMixedOutcome ? completedResponse?.transaction_id || completedResponse?.id :
    paymentStatus?.transaction_id ||
    paymentDetails?.transaction_id ||
    paymentDetails?.id;
  const referenceId = successReferenceId;
  const successCopy =
    Number(amountPaid) > 0
      ? `Your payment of ₹${Number(amountPaid).toLocaleString("en-IN")} was successful. Your request is being processed.`
      : "Your request has been placed successfully and is being processed.";
  const primaryColor = merchantConfig.primary_theme_color || "#3B34F6";

  return (
    <div className="v2-success-shell">
      <PageHeader
        logoSrc={merchantConfig.brand_logo}
        logoAlt={merchantConfig.shop_display_name}
        primaryColor={primaryColor}
        showBack={false}
      />
      {(successRequestId || successReferenceId) && !mixedOperationPending ? (
        <StyledSuccess
          rounded={merchantConfig.use_rounded_edge_buttons}
          primaryColor={primaryColor}
          primarySoft={withAlpha(primaryColor, "33")}
        >
          <div className="v2-success-icon">
            <CheckIcon style={{fontSize: "40px", width: "50px", height: "50px"}} />
          </div>
          <h1 className="v2-success-title">Request Confirmed</h1>
          <p className="v2-success-subtitle">{successCopy}</p>

          <h2 className="v2-success-section-title">What happens next</h2>
          <div className="v2-next-card">
            <div className="v2-next-item">
              <span className="v2-next-icon">
                <AssignmentTurnedInIcon />
              </span>
              <div>
                <p className="v2-next-title">Verification</p>
                <p className="v2-next-copy">
                  The store will review your request and confirm the next steps.
                </p>
              </div>
            </div>
            <div className="v2-next-item">
              <span className="v2-next-icon">
                <LocalShippingIcon />
              </span>
              <div>
                <p className="v2-next-title">Request Processing</p>
                <p className="v2-next-copy">
                  Once approved, the store will arrange any required pickup or service.
                </p>
              </div>
            </div>
            <div className="v2-next-item">
              <span className="v2-next-icon">
                <NotificationsActiveIcon />
              </span>
              <div>
                <p className="v2-next-title">Live Updates</p>
                <p className="v2-next-copy">
                  Track your request to see its latest status and updates.
                </p>
              </div>
            </div>
          </div>

          {(transactionId || referenceId || confirmedAgent) && (
            <div className="v2-request-meta-grid">
              {transactionId && (
                <div>
                  <p className="v2-request-meta-label">Transaction ID</p>
                  <p className="v2-request-meta-value">{transactionId}</p>
                </div>
              )}
              {referenceId && (
                <div>
                  <p className="v2-request-meta-label">Reference ID</p>
                  <p className="v2-request-meta-value">#{referenceId}</p>
                </div>
              )}
              {confirmedAgent && !referenceId && (
                <div>
                  <p className="v2-request-meta-label">Request ID</p>
                  <p className="v2-request-meta-value">#{successRequestId}</p>
                </div>
              )}
              {confirmedAgent && (
                <div>
                  <p className="v2-request-meta-label">Agent draft reference</p>
                  <p className="v2-request-meta-value">{confirmedAgent.reference_id}</p>
                  <p className="v2-next-copy">Saved for the store's agents. No payment was collected for this draft.</p>
                </div>
              )}
            </div>
          )}

          <div className="v2-success-action-bar">
            <Button
              className="v2-back-orders"
              variant="outlined"
              onClick={handleBackToOrders}
            >
              Back to Orders
            </Button>
            <Button
              className="v2-track-request"
              variant="contained"
              onClick={handleTracking}
              endIcon={<ArrowForwardIcon />}
            >
              Track Request
            </Button>
          </div>
        </StyledSuccess>
      ) : (
        <V2TrackingBoneyardLoader />
      )}
    </div>
  );
}

export default Success;
